import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import posterService from '../services/posterService'
import useModalEnter from '../hooks/useModalEnter'

/* Post-login welcome banner — the reference admin opens BootstrapVue's
   `home-modal` once, right after login, on /admin/home. Markup mirrors that
   DOM; all styling (red round close, transparent content, 300px on phones)
   is already in custom.css / responsive.css. Images come from the same public
   poster API the player site uses: desktop poster on web, mobile poster on
   phones (the reference swaps to a portrait image there). */
function WelcomeBannerModal({ onClose }) {
  // Seeded from the boot prefetch (main.jsx) so the banner opens with no wait;
  // getPublic only hits the network if that prefetch hadn't finished.
  const [posters, setPosters] = useState(() => posterService.peekPublic() || [])
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    let alive = true
    posterService.getPublic().then((list) => { if (alive) setPosters(list) }).catch(() => {})
    return () => { alive = false }
  }, [])

  const desktop = posters.find((p) => !p.is_mobile)?.img
  const mobile = posters.find((p) => p.is_mobile)?.img
  const ready = !!(desktop || mobile)
  // Animate from the moment the image is there (not from mount), so the
  // reference's fade + 50px slide-in actually plays.
  const { entered, modalClass, onBackdropClick } = useModalEnter(onClose, ready)

  // BootstrapVue marks the body while a modal is open, and closes it on Esc
  useEffect(() => {
    if (!ready) return undefined
    document.body.classList.add('modal-open')
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('modal-open')
      document.removeEventListener('keydown', onKey)
    }
  }, [ready, onClose])

  // Nothing to show yet (or the API failed) — the reference never opens an empty modal.
  if (!ready) return null

  return createPortal(
    <>
      <div
        role="dialog"
        aria-modal="true"
        className={`${modalClass} home-modal`}
        style={{ display: 'block' }}
        onClick={onBackdropClick}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <span tabIndex={0}></span>
          <div tabIndex={-1} className="modal-content">
            <header className="modal-header">
              <div className="close-home-modal" onClick={onClose}>
                <i className="fas fa-times"></i>
              </div>
            </header>
            <div className="modal-body">
              {/* one poster missing → the other shows at every width */}
              {desktop && <img src={desktop} className={`img-fluid${mobile ? ' d-none-mobile' : ''}`} alt="" />}
              {mobile && <img src={mobile} className={`img-fluid${desktop ? ' d-none-desktop' : ''}`} alt="" />}
            </div>
          </div>
          <span tabIndex={0}></span>
        </div>
      </div>
      {/* BootstrapVue backdrop: `fade` → `fade show` (fades in) → plain `.modal-backdrop` once done */}
      <div
        className={settled ? 'modal-backdrop' : `modal-backdrop fade${entered ? ' show' : ''}`}
        onTransitionEnd={() => entered && setSettled(true)}
      ></div>
    </>,
    document.body
  )
}

export default WelcomeBannerModal
